import type { JSONContent } from '@tiptap/react';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PROTOCOL_NODE_TYPES, SECTION_KINDS, SECTION_TITLES } from '../constants';
import type { ProtocolSectionAttrs, SectionKind } from '../types';
import { upgradeLegacyChip } from './protocol-chip';

/** Ids only need to be unique inside one protocol document. */
export const newLocalId = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

export const newSection = (kind: SectionKind, title: string = SECTION_TITLES[kind]): JSONContent => ({
  type: kind,
  attrs: kind === 'subProtocol' ? { id: newLocalId('sp'), title } : { title },
  content: [{ type: 'paragraph' }]
});

export const isSection = (node: JSONContent): boolean => SECTION_KINDS.includes(node.type as SectionKind);

export const isSectionNode = (node: PMNode): boolean => SECTION_KINDS.includes(node.type.name as SectionKind);

// The section with this id; the fixed two use their type name.
export function findSection(doc: PMNode, id: string): { node: PMNode; pos: number } | null {
  let found: { node: PMNode; pos: number } | null = null;
  doc.forEach((child, offset) => {
    if (!found && isSectionNode(child) && sectionIdOf(child) === id) found = { node: child, pos: offset };
  });
  return found;
}

export const sectionIdOf = (node: PMNode): string =>
  node.type.name === 'subProtocol' ? (node.attrs['id'] as string) : node.type.name;

export interface SectionInfo extends ProtocolSectionAttrs {
  index: number;
}

/** The sections a document holds, in order. */
export function sectionsOf(doc: JSONContent | undefined): SectionInfo[] {
  return (doc?.content ?? []).flatMap((node, index) => {
    if (!isSection(node)) return [];
    const kind = node.type as SectionKind;
    const id = kind === 'subProtocol' ? node.attrs?.['id'] : kind;
    if (typeof id !== 'string') return [];
    return [{ kind, id, title: typeof node.attrs?.['title'] === 'string' ? node.attrs['title'] : '', index }];
  });
}

/** True for text nodes with no text, which ProseMirror rejects. */
function isEmptyTextNode(node: JSONContent): boolean {
  return node.type === 'text' && !node.text;
}

// Drops any node the editor does not know, and empty text nodes; a legacy chip is rewritten in the current shape.
export function sanitizeProtocolDoc(original: JSONContent): JSONContent | null {
  if (original.type && !PROTOCOL_NODE_TYPES.has(original.type)) {
    console.error(`[ProtocolEditor] Unknown node type ignored: ${original.type}`, original);
    return null;
  }
  const node = upgradeLegacyChip(original);
  if (!node.content) return node;
  return {
    ...node,
    content: node.content.flatMap((child) => {
      if (isEmptyTextNode(child)) return [];
      const sanitized = sanitizeProtocolDoc(child);
      return sanitized ? [sanitized] : [];
    })
  };
}

/** Chips store a copy of the name; the section's title wins, here and in the compiler. */
export function syncSubProtocolLabels(doc: JSONContent): JSONContent {
  const titles = new Map<string, string>();
  for (const section of doc.content ?? []) {
    const id = section.attrs?.['id'];
    if (section.type === 'subProtocol' && typeof id === 'string') titles.set(id, String(section.attrs?.['title'] ?? ''));
  }
  if (titles.size === 0) return doc;

  const walk = (node: JSONContent): JSONContent => {
    const subProtocolId = node.attrs?.['subProtocolId'];
    const title = typeof subProtocolId === 'string' ? titles.get(subProtocolId) : undefined;
    const fixed = title !== undefined && node.attrs?.['label'] !== title ? { ...node, attrs: { ...node.attrs, label: title } } : node;
    return fixed.content ? { ...fixed, content: fixed.content.map(walk) } : fixed;
  };

  return walk(doc);
}

// Both fixed sections, in order; loose top-level blocks fold into Instructions.
export function ensureSections(doc: JSONContent | undefined): JSONContent {
  const content = doc?.content ?? [];
  const sections = content.filter(isSection);
  const loose = content.filter((node) => !isSection(node));

  // An older document can hold a section that was pasted inside another one.
  const unwrapNested = (nodes: JSONContent[]): JSONContent[] =>
    nodes.flatMap((node) => (isSection(node) ? unwrapNested(node.content ?? []) : [node]));

  const withParagraph = (section: JSONContent): JSONContent => {
    const inner = unwrapNested(section.content ?? []);
    return { ...section, content: inner.length ? inner : [{ type: 'paragraph' }] };
  };

  const preCheck = sections.find((node) => node.type === 'preCheck') ?? newSection('preCheck');
  const instructions = sections.find((node) => node.type === 'instructions') ?? { ...newSection('instructions'), content: [] };
  const subProtocols = sections.filter((node) => node.type === 'subProtocol');

  const instructionsWithLoose = { ...instructions, content: [...(instructions.content ?? []), ...loose] };

  return {
    type: 'doc',
    ...(doc?.attrs ? { attrs: doc.attrs } : {}),
    content: [withParagraph(preCheck), withParagraph(instructionsWithLoose), ...subProtocols.map(withParagraph)]
  };
}
